import { format } from "date-fns"

export interface Currency {
  code: string
  symbol: string
  name: string
  locale: string
  decimals: number
}

export const currencies: Currency[] = [
  { code: "USD", symbol: "$", name: "US Dollar", locale: "en-US", decimals: 2 },
  { code: "EUR", symbol: "\u20ac", name: "Euro", locale: "de-DE", decimals: 2 },
  { code: "GBP", symbol: "\u00a3", name: "British Pound", locale: "en-GB", decimals: 2 },
  { code: "UAH", symbol: "\u20b4", name: "Ukrainian Hryvnia", locale: "uk-UA", decimals: 2 },
  { code: "PLN", symbol: "z\u0142", name: "Polish Zloty", locale: "pl-PL", decimals: 2 },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar", locale: "en-CA", decimals: 2 },
  { code: "AUD", symbol: "A$", name: "Australian Dollar", locale: "en-AU", decimals: 2 },
  { code: "JPY", symbol: "\u00a5", name: "Japanese Yen", locale: "ja-JP", decimals: 0 },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc", locale: "de-CH", decimals: 2 },
  { code: "INR", symbol: "\u20b9", name: "Indian Rupee", locale: "en-IN", decimals: 2 },
  { code: "CNY", symbol: "\u00a5", name: "Chinese Yuan", locale: "zh-CN", decimals: 2 },
  { code: "BRL", symbol: "R$", name: "Brazilian Real", locale: "pt-BR", decimals: 2 },
  { code: "MXN", symbol: "$", name: "Mexican Peso", locale: "es-MX", decimals: 2 },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar", locale: "en-SG", decimals: 2 },
  { code: "NZD", symbol: "NZ$", name: "New Zealand Dollar", locale: "en-NZ", decimals: 2 },
  { code: "SEK", symbol: "kr", name: "Swedish Krona", locale: "sv-SE", decimals: 2 },
  { code: "KRW", symbol: "\u20a9", name: "South Korean Won", locale: "ko-KR", decimals: 0 },
  { code: "TRY", symbol: "\u20ba", name: "Turkish Lira", locale: "tr-TR", decimals: 2 },
  { code: "ZAR", symbol: "R", name: "South African Rand", locale: "en-ZA", decimals: 2 },
  { code: "AED", symbol: "AED", name: "UAE Dirham", locale: "ar-AE", decimals: 2 },
  { code: "CZK", symbol: "K\u010d", name: "Czech Koruna", locale: "cs-CZ", decimals: 2 },
  { code: "ILS", symbol: "\u20aa", name: "Israeli Shekel", locale: "he-IL", decimals: 2 },
  { code: "THB", symbol: "\u0e3f", name: "Thai Baht", locale: "th-TH", decimals: 2 },
  { code: "MYR", symbol: "RM", name: "Malaysian Ringgit", locale: "ms-MY", decimals: 2 },
]

export function formatMoneyLocale(amount: number, currencyCode: string): string {
  const currency = currencies.find((c) => c.code === currencyCode) || currencies[0]
  try {
    return new Intl.NumberFormat(currency.locale, {
      style: "currency",
      currency: currency.code,
      minimumFractionDigits: currency.decimals,
      maximumFractionDigits: currency.decimals,
    }).format(amount)
  } catch {
    return `${currency.symbol}${amount.toFixed(currency.decimals)}`
  }
}

export type LayoutId = "standard" | "compact" | "modern" | "sidebar" | "split"
export type ColorSchemeId = "blue" | "teal" | "slate" | "violet" | "orange" | "emerald" | "rose" | "zinc"
export type DocumentType = "invoice" | "quote" | "receipt" | "credit-note" | "proforma"
export type InvoiceStatus = "draft" | "sent" | "paid" | "overdue" | "cancelled" | "none"
export type DiscountType = "percentage" | "fixed"
export type ItemUnit = "units" | "hours" | "days" | "items" | "kg" | "hrs-min" | "fixed"

export interface LayoutConfig {
  id: LayoutId
  name: string
  description: string
}

export interface ColorScheme {
  id: ColorSchemeId
  name: string
  accent: string
  tableBg: string
  tableText: string
}

export interface DocumentTypeConfig {
  id: DocumentType
  label: string
  title: string
  description: string
}

export interface StatusConfig {
  id: InvoiceStatus
  label: string
  color: string
  bg: string
}

export const layouts: LayoutConfig[] = [
  { id: "standard", name: "Standard", description: "Logo left, details right" },
  { id: "compact", name: "Compact", description: "Balance due prominent at top" },
  { id: "modern", name: "Modern", description: "Centered header, clean lines" },
  { id: "sidebar", name: "Sidebar", description: "Vertical info bar on left" },
  { id: "split", name: "Split", description: "Two-column with divider" },
]

export const colorSchemes: ColorScheme[] = [
  { id: "blue", name: "Blue", accent: "#1e40af", tableBg: "#1e40af", tableText: "#ffffff" },
  { id: "teal", name: "Teal", accent: "#0d9488", tableBg: "#f0fdfa", tableText: "#134e4a" },
  { id: "slate", name: "Slate", accent: "#0f172a", tableBg: "#0f172a", tableText: "#ffffff" },
  { id: "violet", name: "Violet", accent: "#7c3aed", tableBg: "#f5f3ff", tableText: "#5b21b6" },
  { id: "orange", name: "Orange", accent: "#ea580c", tableBg: "#fff7ed", tableText: "#9a3412" },
  { id: "emerald", name: "Emerald", accent: "#059669", tableBg: "#ecfdf5", tableText: "#047857" },
  { id: "rose", name: "Rose", accent: "#e11d48", tableBg: "#fff1f2", tableText: "#9f1239" },
  { id: "zinc", name: "Zinc", accent: "#18181b", tableBg: "#fafafa", tableText: "#3f3f46" },
]

export const documentTypes: DocumentTypeConfig[] = [
  { id: "invoice", label: "Invoice", title: "INVOICE", description: "Bill for goods or services" },
  { id: "quote", label: "Quote", title: "QUOTE", description: "Price proposal, no payment due" },
  { id: "receipt", label: "Receipt", title: "RECEIPT", description: "Proof of payment received" },
  { id: "credit-note", label: "Credit Note", title: "CREDIT NOTE", description: "Refund or adjustment" },
  { id: "proforma", label: "Proforma", title: "PROFORMA INVOICE", description: "Advance / preliminary invoice" },
]

export const statuses: StatusConfig[] = [
  { id: "none", label: "No badge", color: "#6b7280", bg: "transparent" },
  { id: "draft", label: "Draft", color: "#6b7280", bg: "#f3f4f6" },
  { id: "sent", label: "Sent", color: "#1e40af", bg: "#dbeafe" },
  { id: "paid", label: "Paid", color: "#047857", bg: "#d1fae5" },
  { id: "overdue", label: "Overdue", color: "#b91c1c", bg: "#fee2e2" },
  { id: "cancelled", label: "Cancelled", color: "#52525b", bg: "#e4e4e7" },
]

export const itemUnits: { id: ItemUnit; label: string; suffix: string; rateLabel: string }[] = [
  { id: "units", label: "Quantity", suffix: "", rateLabel: "Rate" },
  { id: "hours", label: "Hours", suffix: " hr", rateLabel: "Hourly rate" },
  { id: "days", label: "Days", suffix: " d", rateLabel: "Day rate" },
  { id: "items", label: "Items", suffix: " ea", rateLabel: "Unit price" },
  { id: "kg", label: "Kilograms", suffix: " kg", rateLabel: "Price/kg" },
  { id: "fixed", label: "Flat fee", suffix: "", rateLabel: "Amount" },
]

export const paymentTermsPresets: { label: string; days: number | null }[] = [
  { label: "Due on receipt", days: 0 },
  { label: "Net 7", days: 7 },
  { label: "Net 14", days: 14 },
  { label: "Net 15", days: 15 },
  { label: "Net 30", days: 30 },
  { label: "Net 45", days: 45 },
  { label: "Net 60", days: 60 },
  { label: "Net 90", days: 90 },
  { label: "Custom", days: null },
]

export interface LineItem {
  id: string
  description: string
  details?: string
  quantity: number
  rate: number
  unit?: ItemUnit
  discount?: number
  discountType?: DiscountType
  taxRate?: number
}

export interface TaxLine {
  id: string
  name: string
  rate: number
  compound?: boolean
}

export interface SavedItem {
  id: string
  description: string
  rate: number
  unit: ItemUnit
}

export interface SavedInvoiceMeta {
  id: string
  invoiceNumber: string
  billTo: string
  total: number
  currency: string
  issueDate: string
  status: InvoiceStatus
  documentType: DocumentType
  updatedAt: string
}

export interface InvoiceData {
  invoiceNumber: string
  documentType: DocumentType
  status: InvoiceStatus
  issueDate: string
  dueDate: string
  paymentTerms: string
  logo: string | null
  from: string
  fromTaxId: string
  billTo: string
  billToTaxId: string
  shipTo: string
  poNumber: string
  items: LineItem[]
  notes: string
  terms: string
  paymentInfo: string
  currency: string
  layout: LayoutId
  colorScheme: ColorSchemeId
  taxRate: number
  taxName: string
  taxes: TaxLine[]
  taxInclusive: boolean
  discountRate: number
  discountType: DiscountType
  showDiscount: boolean
  shipping: number
  showShipping: boolean
  amountPaid: number
  showPerItemTax: boolean
  showPerItemDiscount: boolean
  defaultItemUnit: ItemUnit
  paymentLink: string
  lateFeeRate: number
}

export const defaultInvoice: InvoiceData = {
  invoiceNumber: "INV-0001",
  documentType: "invoice",
  status: "none",
  issueDate: format(new Date(), "yyyy-MM-dd"),
  dueDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
  paymentTerms: "Net 30",
  logo: null,
  from: "",
  fromTaxId: "",
  billTo: "",
  billToTaxId: "",
  shipTo: "",
  poNumber: "",
  items: [{ id: crypto.randomUUID(), description: "", quantity: 1, rate: 0, unit: "units" }],
  notes: "",
  terms: "",
  paymentInfo: "",
  currency: "USD",
  layout: "standard",
  colorScheme: "blue",
  taxRate: 0,
  taxName: "Tax",
  taxes: [],
  taxInclusive: false,
  discountRate: 0,
  discountType: "percentage",
  showDiscount: false,
  shipping: 0,
  showShipping: false,
  amountPaid: 0,
  showPerItemTax: false,
  showPerItemDiscount: false,
  defaultItemUnit: "units",
  paymentLink: "",
  lateFeeRate: 0,
}

export const exampleInvoice: Partial<InvoiceData> = {
  invoiceNumber: "INV-2026-001",
  from: "Sarah Chen Design Studio\n123 Designer Way, Suite 200\nBerlin 10115, Germany\nhello@sarahchen.studio\n+49 30 1234 5678",
  fromTaxId: "DE123456789",
  billTo: "Lumen Tech GmbH\n456 Innovation Avenue\nHamburg 20095, Germany\nbilling@lumentech.de",
  billToTaxId: "DE987654321",
  poNumber: "PO-2026-042",
  items: [
    {
      id: crypto.randomUUID(),
      description: "Brand identity design",
      details: "Logo concepts, color palette, typography system, brand guidelines doc",
      quantity: 32,
      rate: 120,
      unit: "hours",
    },
    {
      id: crypto.randomUUID(),
      description: "Website UI design",
      details: "5 key pages — homepage, product, pricing, blog, contact (desktop + mobile)",
      quantity: 18,
      rate: 120,
      unit: "hours",
    },
    {
      id: crypto.randomUUID(),
      description: "Project management & client meetings",
      quantity: 6,
      rate: 95,
      unit: "hours",
    },
  ],
  notes: "Thank you for the great project! Please pay within 30 days via the bank details below.",
  terms: "Late payments incur 5% interest per month. Files remain property of Sarah Chen Design Studio until full payment is received.",
  paymentInfo: "Bank: Deutsche Bank\nIBAN: DE89 3704 0044 0532 0130 00\nBIC: DEUTDEFF\nRef: INV-2026-001",
  taxes: [{ id: crypto.randomUUID(), name: "VAT", rate: 19 }],
  currency: "EUR",
}

export const STORAGE_KEY = "wkit-invoice-v7"
export const HISTORY_KEY = "wkit-invoice-history-v1"
export const PRESETS_KEY = "wkit-invoice-presets-v1"
export const HISTORY_MAX = 25

export interface InvoiceTotals {
  subtotal: number
  itemDiscountTotal: number
  itemTaxTotal: number
  globalDiscountAmount: number
  globalTaxAmount: number
  shippingAmount: number
  total: number
  balanceDue: number
}

export function calculateLineNet(item: LineItem): number {
  const gross = item.quantity * item.rate
  if (!item.discount) return gross
  if (item.discountType === "fixed") return Math.max(0, gross - item.discount)
  return gross * (1 - item.discount / 100)
}

export function calculateLineTax(item: LineItem): number {
  if (!item.taxRate) return 0
  return calculateLineNet(item) * (item.taxRate / 100)
}

export function calculateTotals(invoice: InvoiceData): InvoiceTotals {
  let subtotal = 0
  let itemDiscountTotal = 0
  let itemTaxTotal = 0

  for (const item of invoice.items) {
    const gross = item.quantity * item.rate
    const net = calculateLineNet(item)
    subtotal += gross
    itemDiscountTotal += gross - net
    if (invoice.showPerItemTax) {
      itemTaxTotal += calculateLineTax(item)
    }
  }

  const afterItemDiscount = subtotal - itemDiscountTotal

  let globalDiscountAmount = 0
  if (invoice.showDiscount) {
    globalDiscountAmount =
      invoice.discountType === "fixed"
        ? Math.min(invoice.discountRate, afterItemDiscount)
        : afterItemDiscount * (invoice.discountRate / 100)
  }

  const taxableBase = afterItemDiscount - globalDiscountAmount

  let globalTaxAmount = 0
  if (invoice.taxes.length > 0) {
    let runningBase = taxableBase
    for (const tax of invoice.taxes) {
      const amount = runningBase * (tax.rate / 100)
      globalTaxAmount += amount
      if (tax.compound) runningBase += amount
    }
  } else if (invoice.taxRate > 0) {
    globalTaxAmount = taxableBase * (invoice.taxRate / 100)
  }

  const shippingAmount = invoice.showShipping ? invoice.shipping : 0

  let total: number
  if (invoice.taxInclusive) {
    total = taxableBase + itemTaxTotal + shippingAmount
  } else {
    total = taxableBase + globalTaxAmount + itemTaxTotal + shippingAmount
  }

  const balanceDue = total - invoice.amountPaid

  return {
    subtotal,
    itemDiscountTotal,
    itemTaxTotal,
    globalDiscountAmount,
    globalTaxAmount,
    shippingAmount,
    total,
    balanceDue,
  }
}

export function formatQuantity(item: LineItem): string {
  const unitMeta = itemUnits.find((u) => u.id === (item.unit || "units"))
  const suffix = unitMeta?.suffix || ""
  if (item.unit === "hours" || item.unit === "days" || item.unit === "kg") {
    return `${item.quantity.toLocaleString("en-US", { maximumFractionDigits: 2 })}${suffix}`
  }
  if (item.unit === "fixed") return "—"
  return `${item.quantity}${suffix}`
}


const numberSuffixRegex = /(\d+)(?!.*\d)/

/**
 * Suggest the next invoice number from the previous one by incrementing the
 * trailing digit run, preserving its width with leading zeros.
 *  - "INV-0001"     -> "INV-0002"
 *  - "INV-2026-042" -> "INV-2026-043"
 *  - "123"          -> "124"
 *  - ""             -> "INV-0001"
 *  - "draft"        -> "draft-1"
 */
export function nextInvoiceNumber(previous: string): string {
  const trimmed = previous?.trim() ?? ""
  if (!trimmed) return "INV-0001"

  const match = trimmed.match(numberSuffixRegex)
  if (!match) return `${trimmed}-1`

  const digits = match[1]
  const width = digits.length
  const incremented = (Number.parseInt(digits, 10) + 1).toString().padStart(width, "0")
  return trimmed.slice(0, match.index!) + incremented
}

/**
 * Compute the number of days an invoice is overdue.
 * Returns 0 when not overdue, when no due date is set, or when the date is unparseable.
 */
export function daysOverdue(dueDate: string, today: Date = new Date()): number {
  if (!dueDate) return 0
  const due = new Date(dueDate)
  if (Number.isNaN(due.getTime())) return 0
  // Compare at day boundary, ignoring time of day.
  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  const dueUtc = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate())
  const ms = todayUtc - dueUtc
  return ms > 0 ? Math.floor(ms / 86_400_000) : 0
}

/** Compute a late fee based on a percentage rate and the current subtotal/total. */
export function computeLateFee(amount: number, ratePercent: number, days: number): number {
  if (ratePercent <= 0 || days <= 0 || amount <= 0) return 0
  // Treat ratePercent as monthly; pro-rate to actual days overdue.
  return Math.round(((amount * ratePercent) / 100) * (days / 30) * 100) / 100
}

function csvEscape(value: string | number): string {
  const s = String(value)
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

/** Serialize the invoice items to CSV. The header row is included. */
export function itemsToCsv(invoice: InvoiceData): string {
  const header = ["#", "Description", "Details", "Quantity", "Unit", "Unit price", "Discount", "Tax %", "Line total"]
  const rows = invoice.items.map((item, index) => {
    const net = calculateLineNet(item)
    const discount = item.discountType === "percentage"
      ? `${item.discount ?? 0}%`
      : (item.discount ?? 0).toString()
    return [
      index + 1,
      csvEscape(item.description ?? ""),
      csvEscape(item.details ?? ""),
      item.quantity,
      csvEscape(item.unit ?? invoice.defaultItemUnit),
      item.rate,
      csvEscape(discount),
      item.taxRate ?? 0,
      net.toFixed(2),
    ].join(",")
  })
  return [header.map(csvEscape).join(","), ...rows].join("\n")
}
